import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'
import type { Subscription, SubscriptionAgent, SubscriptionOrder } from '@entities/subscription'

import { OrderStatus } from '@brighterly/lib-core-types/orders/enums'
import { SubscriptionProvider, SubscriptionStatus } from '@brighterly/lib-core-types/subscription/enums'

export const SUBSCRIPTION_EMPTY_CELL = '-'

/** The deal-owner select needs a concrete value for "no agent"; user ids start at 1, so `0` is free. */
export const NO_AGENT_VALUE = 0
const NO_AGENT_TEXT = 'PAD self-service'

const PERCENT = 100

const PRODUCT_CHANGE_PROVIDERS: SubscriptionProvider[] = [SubscriptionProvider.Solidgate, SubscriptionProvider.Stripe]
const PAUSABLE_STATUSES: SubscriptionStatus[] = [SubscriptionStatus.Ongoing, SubscriptionStatus.Failed]
const CANCELABLE_STATUSES: SubscriptionStatus[] = [
  SubscriptionStatus.Ongoing,
  SubscriptionStatus.Failed,
  SubscriptionStatus.Paused,
]

export const isSubscriptionFailed = (subscription: Subscription): boolean =>
  subscription.status === SubscriptionStatus.Failed

export const isPauseStarted = (subscription: Subscription): boolean =>
  !!subscription.paused_to && subscription.status === SubscriptionStatus.Paused

/** A pause is booked but has not begun yet. */
export const isPausePlanned = (subscription: Subscription): boolean =>
  !!subscription.paused_from && !!subscription.paused_to && subscription.status !== SubscriptionStatus.Paused

export const canChangeProduct = (subscription: Subscription): boolean =>
  subscription.status === SubscriptionStatus.Ongoing && PRODUCT_CHANGE_PROVIDERS.includes(subscription.provider)

/** A product applies to every discipline at once, so a shared subscription has to be split first. */
export const isProductChangeLocked = (subscription: Subscription): boolean => subscription.disciplines.length > 1

export const canApplyDiscount = (subscription: Subscription): boolean =>
  subscription.status === SubscriptionStatus.Ongoing && !subscription.paused_from && !subscription.paused_to

export const canResume = (subscription: Subscription): boolean =>
  subscription.status === SubscriptionStatus.Paused || !!subscription.paused_to

export const canPause = (subscription: Subscription): boolean =>
  PAUSABLE_STATUSES.includes(subscription.status) && !subscription.paused_to

/** Only a Stripe pause can be moved once booked; every other provider keeps the dates it was given. */
export const canChangePause = (subscription: Subscription): boolean =>
  subscription.provider === SubscriptionProvider.Stripe && (isPauseStarted(subscription) || isPausePlanned(subscription))

export const canCancel = (subscription: Subscription): boolean => CANCELABLE_STATUSES.includes(subscription.status)

/** The API decides: it sends `charge_now` only for an ongoing Stripe subscription with a payment ahead. */
export const canChargeNow = (subscription: Subscription): boolean => !!subscription.charge_now

export const isChargeNowAvailable = (subscription: Subscription): boolean => !!subscription.charge_now?.is_available

/** Only a provider-side subscription can be charged again. */
export const canRetryPayment = (order: SubscriptionOrder, subscription: Subscription): boolean =>
  order.status === OrderStatus.FAILED && !!subscription.external_id

/** Whole cents, as the API bills them. */
export const applyDiscount = (cents: number, discount: number): number =>
  Math.round(cents - (cents * discount) / PERCENT)

export const toAgentValue = (agentId: number | null): number => agentId ?? NO_AGENT_VALUE

export const fromAgentValue = (value: number): number | null => (value === NO_AGENT_VALUE ? null : value)

/** "PAD self-service" first, then the active agents — plus the current owner when they are no longer active. */
export const getAgentOptions = (agents: SubscriptionAgent[], subscription: Subscription): AppSelectOption<number>[] => {
  const options: AppSelectOption<number>[] = [
    { value: NO_AGENT_VALUE, text: NO_AGENT_TEXT },
    ...agents.map(({ id, name }) => ({ value: id, text: name })),
  ]

  const { agent } = subscription

  if (agent && !agents.some(({ id }) => id === agent.id)) {
    options.push({ value: agent.id, text: agent.name })
  }

  return options
}
